import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorWithRetry } from "@/components/error-with-retry";
import { isPageLoadError, recoverPageLoad } from '@/lib/page-load-recovery';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Label shown to the user — defaults to "this section". */
  context?: string;
  /** Optional fallback override. If provided, renders instead of the default. */
  fallback?: (reset: () => void, error: Error | null) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches runtime errors in its subtree so a crash in one widget (TelemetryCharts,
 * TrackMap, etc.) doesn't tear down the whole app. Pair with route-level boundaries
 * in App.tsx and optionally panel-level boundaries inside Dashboard.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface for whatever monitoring is wired up. Falls back to console
    // when no global error reporter is registered.
    if (typeof window !== "undefined") {
      const reporter = (window as unknown as { onComponentError?: (e: Error, i: ErrorInfo) => void })
        .onComponentError;
      if (reporter) {
        try {
          reporter(error, info);
        } catch {
          // swallow — don't recurse
        }
      }
    }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    recoverPageLoad(error);
  }

  reset = () => {
    if (isPageLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(this.reset, error);
    }

    return (
      <div className="p-4">
        <ErrorWithRetry
          message={isPageLoadError(error) ? 'This page could not finish loading. Please try again.' : `Something went wrong${this.props.context ? ` in ${this.props.context}` : ""}.`}
          onRetry={this.reset}
        />
      </div>
    );
  }
}
